import type { OpeningAreaPropTemplate } from '../../../../shared/scene/openingAreaPropTemplates';
import { townColorHex as hex, type TownPropPalette } from './townPropTemplates';

/** No shared SVG IDs, filters or external assets: bodies can be composed safely.
 * Stone/primary/shade describe masonry, wood/woodEdge timber and bronze inlay,
 * iron the deepest joints, and glass/glassCore the restrained mineral accents.
 */
function archStone(index: number, p: TownPropPalette): string {
  const start = Math.PI + index * Math.PI / 13 + 0.012;
  const end = Math.PI + (index + 1) * Math.PI / 13 - 0.012;
  const point = (angle: number, rx: number, ry: number) =>
    `${(200 + Math.cos(angle) * rx).toFixed(2)} ${(149 + Math.sin(angle) * ry).toFixed(2)}`;
  const a = point(start, 171, 137), b = point(end, 171, 137);
  const c = point(end, 103, 85), d = point(start, 103, 85);
  return `<g data-part="vault-voussoir">
    <path d="M${a}A171 137 0 0 1 ${b}L${c}A103 85 0 0 0 ${d}Z" fill="${hex(index % 3 === 0 ? p.primary : p.stone)}" />
    <path d="M${point(start + 0.017, 165, 130)}A165 130 0 0 1 ${point(end - 0.017, 165, 130)}"
      fill="none" stroke="${hex(p.woodEdge)}" stroke-width="2" opacity="0.52" />
    <path d="M${d}A103 85 0 0 1 ${c}L${point(end, 111, 92)}A111 92 0 0 0 ${point(start, 111, 92)}Z"
      fill="${hex(p.shade)}" />
  </g>`;
}

function archPier(mirrored: boolean, p: TownPropPalette): string {
  const transform = mirrored ? 'translate(400 0) scale(-1 1)' : 'translate(0 0)';
  const courses = [159, 198, 237, 276, 315].map((y, i) => {
    const split = i % 2 ? 57 : 73;
    return `<path d="M32 ${y}H96V${y + 35}H32Z" fill="${hex(i % 2 ? p.primary : p.stone)}" />
      <path d="M34 ${y + 3}H93 M${split} ${y + 1}V${y + 34}" stroke="${hex(p.woodEdge)}" stroke-width="1.1" opacity="0.5" />
      <path d="M32 ${y + 36}H97 M${split + 2} ${y + 2}V${y + 36}" stroke="${hex(p.shade)}" stroke-width="2" />`;
  }).join('');
  return `<g data-part="vault-pier" transform="${transform}">
    <path d="M25 149H108V354H22V314L26 307V237L22 231V193L25 188Z" fill="${hex(p.shade)}" />
    ${courses}
    <path d="M96 154H108V351L96 357Z" fill="${hex(p.iron)}" />
    <path d="M28 159V344 M33 164V189 M33 281V305" stroke="${hex(p.woodEdge)}" stroke-width="2" opacity="0.62" />
    <path d="M18 144L25 134H109L119 143V155H18Z" fill="${hex(p.stone)}" />
    <path d="M22 139H111 M21 150H116" stroke="${hex(p.woodEdge)}" stroke-width="2" opacity="0.6" />
    <path d="M42 205H84V265H42Z" fill="${hex(p.iron)}" />
    <path d="M45 208H81V261H45Z" fill="${hex(p.woodEdge)}" />
    <path d="M49 212H77V257H49Z" fill="${hex(p.shade)}" />
    <path d="M63 218L70 228L63 239L56 228Z M63 239V250 M55 249H71"
      fill="none" stroke="${hex(p.glass)}" stroke-width="1.9" opacity="0.78" />
    <circle cx="47" cy="210" r="1.6" fill="${hex(p.glassCore)}" />
    <circle cx="79" cy="259" r="1.6" fill="${hex(p.glassCore)}" />
    <path d="M79 282L70 291L77 300L69 308 M39 172L45 180L39 186 M83 325L76 333"
      fill="none" stroke="${hex(p.iron)}" stroke-width="1.6" opacity="0.72" />
    <path d="M19 348H114L123 358V371H13V359Z" fill="${hex(p.stone)}" />
    <path d="M20 351H111L118 357H16Z" fill="${hex(p.woodEdge)}" opacity="0.58" />
    <path d="M13 365H123V373H13Z" fill="${hex(p.shade)}" />
    <path d="M10 372H126L132 380H5Z" fill="${hex(p.stone)}" />
    <path d="M21 374L48 373 M87 375H117" stroke="${hex(p.woodEdge)}" stroke-width="1.3" opacity="0.5" />
  </g>`;
}

/** 400×380: the passage remains transparent all the way to the floor. */
function vaultArchway(p: TownPropPalette): string {
  return `<g data-part="vault-archway">
    <path d="M23 352V149A177 145 0 0 1 377 149V352H287V149A87 73 0 0 0 113 149V352Z"
      fill="${hex(p.iron)}" />
    <path d="M30 150A170 138 0 0 1 370 150L348 155A148 119 0 0 0 52 155Z" fill="${hex(p.shade)}" />
    ${Array.from({ length: 13 }, (_, i) => archStone(i, p)).join('')}
    <path d="M101 153A99 83 0 0 1 299 153" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="3" opacity="0.8" />
    <path d="M109 156A91 77 0 0 1 291 156" fill="none" stroke="${hex(p.shade)}" stroke-width="5" />
    ${archPier(false, p)}${archPier(true, p)}
    <path d="M180 9L188 3H213L221 11L215 58L201 78L186 61Z" fill="${hex(p.primary)}" />
    <path d="M186 12H215L210 55L201 68L192 56Z" fill="${hex(p.woodEdge)}" />
    <path d="M191 16H210L206 53L201 61L196 53Z" fill="${hex(p.shade)}" />
    <path d="M201 22L206 34L201 48L196 34Z" fill="${hex(p.glass)}" opacity="0.85" />
    <path d="M201 27V39" stroke="${hex(p.glassCore)}" stroke-width="1.4" />
    <path d="M73 52L80 60L74 69L83 78 M327 50L319 60L325 69L315 81 M130 29L136 41L132 51"
      fill="none" stroke="${hex(p.iron)}" stroke-width="1.7" opacity="0.65" />
    <path d="M38 127L48 114L52 115L48 124L43 126Z M338 99L344 92L351 96L348 107L342 105Z"
      fill="${hex(p.woodEdge)}" opacity="0.7" />
    <path d="M140 92L146 95 M251 95L257 92 M113 113L119 117 M281 118L287 114"
      stroke="${hex(p.glass)}" stroke-width="2" opacity="0.55" />
  </g>`;
}

function ossuaryNiche(y: number, index: number, p: TownPropPalette): string {
  return `<g data-part="ossuary-niche" transform="translate(0 ${y})">
    <path d="M29 67V27A31 26 0 0 1 91 27V67Z" fill="${hex(p.woodEdge)}" />
    <path d="M33 65V28A27 23 0 0 1 87 28V65Z" fill="${hex(p.iron)}" />
    <path d="M39 61V29A21 19 0 0 1 81 29V61Z" fill="${hex(p.shade)}" />
    <path d="M40 60V28A20 18 0 0 1 80 28" fill="none" stroke="${hex(p.stone)}" stroke-width="1.5" opacity="0.45" />
    <path d="M45 46Q41 35 47 28Q59 20 70 28Q77 35 72 46L68 49V54H49V49Z" fill="${hex(p.stone)}" />
    <path d="M47 32Q57 26 66 30" fill="none" stroke="${hex(p.primary)}" stroke-width="2.2" />
    <path d="M47 37L54 35L55 42L49 44Z M62 36L70 38L67 44L61 42Z M58 44L55 49H61Z" fill="${hex(p.iron)}" />
    <path d="M52 50V54 M57 50V55 M62 50V54" stroke="${hex(p.shade)}" stroke-width="1.2" />
    <path d="M41 58L77 61 M43 62L75 57" stroke="${hex(p.stone)}" stroke-width="3.1" stroke-linecap="round" />
    <path d="M26 66H94V74H26Z" fill="${hex(p.stone)}" />
    <path d="M30 67H90" stroke="${hex(p.woodEdge)}" stroke-width="1.5" />
    <path d="M55 70H65" stroke="${hex(index === 1 ? p.glassCore : p.glass)}" stroke-width="1.5" opacity="0.7" />
  </g>`;
}

/** 120×340: stacked burial niches are cut into a fluted stone support. */
function vaultOssuary(p: TownPropPalette): string {
  return `<g data-part="vault-ossuary">
    <path d="M16 29H104V315H16Z" fill="${hex(p.shade)}" />
    <path d="M14 32H29V314H14Z M91 32H106V314H91Z" fill="${hex(p.stone)}" />
    <path d="M18 36H23V305H18Z M95 36H100V305H95Z" fill="${hex(p.primary)}" />
    <path d="M25 39V307 M103 40V306" stroke="${hex(p.iron)}" stroke-width="2" opacity="0.8" />
    <path d="M16 84H29 M91 84H105 M15 155H29 M91 155H106 M15 227H29 M91 227H106 M16 294H29 M91 294H105"
      stroke="${hex(p.woodEdge)}" stroke-width="1.4" opacity="0.5" />
    ${[40, 121, 202].map((y, i) => ossuaryNiche(y, i, p)).join('')}
    <path d="M8 23L17 13H103L113 23V34H8Z" fill="${hex(p.stone)}" />
    <path d="M13 23H107 M15 29H105" stroke="${hex(p.woodEdge)}" stroke-width="1.8" opacity="0.7" />
    <path d="M21 12L28 5L47 5L53 1H75L81 5H95L101 12Z" fill="${hex(p.primary)}" />
    <path d="M46 12L59 6L73 12L60 23Z" fill="${hex(p.shade)}" />
    <path d="M59 10L65 14L60 19L55 14Z" fill="${hex(p.glass)}" />
    <path d="M33 287H87V308H33Z" fill="${hex(p.iron)}" />
    <path d="M38 292H82 M44 298L50 293L55 299L62 293L69 299L75 294" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.5" />
    <path d="M18 313H103L112 321V334H8V322Z" fill="${hex(p.stone)}" />
    <path d="M12 321H108 M13 327H107" stroke="${hex(p.woodEdge)}" stroke-width="1.5" opacity="0.65" />
    <path d="M7 332H113L117 340H3Z" fill="${hex(p.primary)}" />
    <path d="M19 176L23 185L19 193 M99 51L95 60L100 67 M78 320L72 330" fill="none" stroke="${hex(p.iron)}" stroke-width="1.3" />
  </g>`;
}

/** 180×250. Crossbeam starts at y=0 below the existing map roof; no roof is drawn. */
function hearthWell(p: TownPropPalette): string {
  return `<g data-part="hearth-well-frame">
    <g data-part="well-timber-frame">
      <path d="M21 12H39V228H21Z M142 12H160V228H142Z" fill="${hex(p.wood)}" />
      <path d="M24 20H29V218H24Z M145 20H150V218H145Z" fill="${hex(p.woodEdge)}" opacity="0.62" />
      <path d="M34 30L32 63L35 74L32 116L34 145 M155 22L153 57L156 68L152 131L155 164"
        fill="none" stroke="${hex(p.iron)}" stroke-width="1.2" opacity="0.6" />
      <path d="M36 56L73 17H61L36 43Z M145 55L107 17H120L145 43Z" fill="${hex(p.wood)}" />
      <path d="M40 46L63 21 M141 45L117 21" stroke="${hex(p.woodEdge)}" stroke-width="1.4" />
      <path d="M10 0H170V20L159 23H20L10 19Z" fill="${hex(p.wood)}" />
      <path d="M12 3H167 M17 13L55 12L63 15H117L128 12H163" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.6" />
      <path d="M19 0H26V23H19Z M151 0H158V23H151Z M21 147H39V155H21Z M142 147H160V155H142Z" fill="${hex(p.iron)}" />
      <circle cx="22.5" cy="10" r="1.5" fill="${hex(p.stone)}" />
      <circle cx="154.5" cy="10" r="1.5" fill="${hex(p.stone)}" />
    </g>
    <g data-part="well-windlass">
      <path d="M29 86H160V94H29Z" fill="${hex(p.iron)}" />
      <path d="M50 78Q87 74 124 78V100Q88 106 50 100Z" fill="${hex(p.wood)}" />
      <ellipse cx="50" cy="89" rx="6" ry="11" fill="${hex(p.woodEdge)}" />
      <path d="M59 81L116 80 M58 96L118 97 M60 87H117" stroke="${hex(p.woodEdge)}" stroke-width="1.3" />
      <path d="M64 78V102 M113 78V102" stroke="${hex(p.iron)}" stroke-width="4" />
      <path d="M78 77V101 M82 76V102 M86 76V103 M90 76V103 M94 76V103" stroke="${hex(p.stone)}" stroke-width="2" />
      <path d="M159 90H171V110H159" fill="none" stroke="${hex(p.iron)}" stroke-width="5" stroke-linejoin="round" />
      <path d="M159 105V116" stroke="${hex(p.woodEdge)}" stroke-width="7" stroke-linecap="round" />
      <circle cx="30" cy="90" r="6" fill="${hex(p.iron)}" />
      <circle cx="30" cy="90" r="2.3" fill="${hex(p.stone)}" />
    </g>
    <g data-part="well-stone-cistern">
      <path d="M20 184H160V228Q90 266 20 228Z" fill="${hex(p.stone)}" />
      <path d="M124 189H160V228Q145 239 122 242Z" fill="${hex(p.shade)}" />
      <path d="M22 210Q90 243 158 210 M23 228Q90 259 157 228 M46 199V220 M77 207V228 M111 205V226 M136 218V235 M60 226V241 M94 231V246"
        fill="none" stroke="${hex(p.iron)}" stroke-width="1.4" opacity="0.6" />
      <ellipse cx="90" cy="184" rx="72" ry="22" fill="${hex(p.primary)}" />
      <ellipse cx="90" cy="183" rx="54" ry="14" fill="${hex(p.iron)}" />
      <ellipse cx="90" cy="190" rx="44" ry="5" fill="${hex(p.glass)}" opacity="0.32" />
      <path d="M61 190Q84 194 108 190" fill="none" stroke="${hex(p.glassCore)}" stroke-width="1" opacity="0.55" />
    </g>
    <g data-part="well-rope-and-bucket">
      <path d="M88 101V151" stroke="${hex(p.stone)}" stroke-width="2.8" />
      <path d="M88 104L90 110L88 116L90 122L88 128L90 134L88 140" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="0.9" />
      <path d="M74 156V149Q88 130 102 149V156" fill="none" stroke="${hex(p.iron)}" stroke-width="2.4" />
      <path d="M72 152H105L101 184Q88 192 76 184Z" fill="${hex(p.wood)}" />
      <path d="M77 155L80 183 M86 155V187 M97 155L95 184" stroke="${hex(p.woodEdge)}" stroke-width="1.6" />
      <ellipse cx="88.5" cy="152" rx="16.5" ry="4.3" fill="${hex(p.iron)}" />
      <path d="M74 161Q88 167 103 161 M76 179Q88 185 101 179" fill="none" stroke="${hex(p.iron)}" stroke-width="3" />
      <circle cx="79" cy="162" r="1.1" fill="${hex(p.stone)}" />
      <circle cx="96" cy="181" r="1.1" fill="${hex(p.stone)}" />
    </g>
    <g data-part="well-front-coping">
      <path d="M18 184Q90 224 162 184V197Q90 236 18 197Z" fill="${hex(p.stone)}" />
      <path d="M20 184Q90 223 160 184" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="2" />
      <path d="M43 196V207 M70 203V216 M100 204V218 M130 198V210" stroke="${hex(p.shade)}" stroke-width="1.7" />
      <path d="M27 243H151L163 250H15Z" fill="${hex(p.stone)}" />
      <path d="M5 250L9 243L18 241L23 250Z M160 250L164 244L171 242L176 250Z" fill="${hex(p.shade)}" />
      <path d="M32 241L52 245 M118 245L146 240" stroke="${hex(p.woodEdge)}" stroke-width="1.4" opacity="0.55" />
    </g>
  </g>`;
}

export function buildOpeningAreaPropBody(template: OpeningAreaPropTemplate, palette: TownPropPalette): string {
  switch (template) {
  case 'vault_archway': return vaultArchway(palette);
  case 'vault_ossuary': return vaultOssuary(palette);
  case 'hearth_well_frame': return hearthWell(palette);
  }
}
